/**
 * Slash commands in the idea box.
 *
 * The box is where somebody states what they want looked at, so it is also where they
 * should be able to say how. This module is the whole of that: text in, a run spec
 * and a clean seed out.
 *
 * THE ONE HARD RULE, and everything else here follows from it: no command text ever
 * reaches `session.seed`. That string is the exploration's title in the library, the
 * `<h1>` of four export formats, the stem of every export filename, and the copy
 * carried in `Digest.seed` and `ForeignRef.seed` when another exploration references
 * this one — and the codec clamps it to 400 characters. A seed with `/check every`
 * left in it is a title with `/check every` in it, forever, in five places.
 *
 * Pure, and deliberately: no store, no `Date.now()`, no ids. `/focus` therefore
 * resolves to a BOOLEAN here rather than to a node — which node is selected is the
 * caller's business, and a parser that reached for the store could not be tested
 * against a string.
 */

import type { DeriveOp, Directive, Frame, RunVerb, UntilCondition } from '../core/types'
import { OP_HINT, OP_LABEL, untilLabel } from '../core/types'
// A type and only a type: `verbatimModuleSyntax` erases it, so the parser stays
// pure of the store at runtime — the same rule `core/types.ts` holds itself to.
import type { RunMode } from '../core/store'
import { LADDER } from './runPolicy'

/** The move counts the budget rows offer. A command naming anything else is snapped
 *  to the nearest and says so, rather than being silently taken as the default.
 *  Exported for `suggest.ts`, whose move cues may only name rows a radio button
 *  actually carries. */
export const MOVE_ROWS = [12, 24, 48] as const

/** The verbs a run may be steered onto — `LADDER`'s, and the conjecture pass.
 *
 *  `LADDER` itself does not change: `conjecture` is reachable only by asking for it,
 *  with `/only insight` (or the id, `/only conjecture`) or a `/ladder` that names it,
 *  and the run dispatches it through the conjecture runner, never as a packet angle
 *  (D281). "Insight" is the button's word for the pass, so `BY_LABEL` takes it
 *  with no entry of its own (D333).
 *
 *  `bridge` is the closing scan and targets no node; `revise` and `outcome` re-derive
 *  from something the USER wrote and cannot be chosen by a policy. Offering any of
 *  the three would accept a command that then matched no move for the whole run. */
export const STEERABLE: readonly DeriveOp[] = [...LADDER, 'conjecture']

/** What the commands asked for. Every field optional; an absent one means untouched. */
export interface CommandSpec {
  moves?: number
  usdMax?: number
  checks?: number | 'every'
  panels?: number
  audits?: number
  /** Conjecture passes for the next run — `/insight` (or `/conjecture`), off unless
   *  typed (D281, D333). */
  conjectures?: number
  ladder?: DeriveOp[]
  depth?: number
  /** Whether `/focus` was given. The caller resolves it against the selection. */
  focus?: boolean
  /** Stop the moment this is met. `/until 3 refuted`, `/until split`, … */
  until?: UntilCondition
  /** How the seed is read — a claim, a question, or a goal. See `Frame`. */
  frame?: Frame
  /** Whether the run takes its moves alone or asks first — `/auto`, `/confirm`. */
  mode?: RunMode
  /** The standing brief, accumulated in the order typed. Several of one kind is
   *  normal — two constraints are two constraints. */
  directives?: Directive[]
  /** Whether `/replicate` was given: after the first model opens the idea, a
   *  model from the other house opens it again, blind. */
  replicate?: boolean
}

/**
 * The run controls a draft resolves against.
 *
 * Every field is REQUIRED and `undefined` is a value rather than an omission, which is
 * the opposite of `CommandSpec` and deliberately so: this is the complete state of the
 * tune panel's run controls, and a partial one would let `applySpec` return a control
 * the caller then has to guess a default for.
 *
 * `ceiling` is a string because the field that holds it is a text input and empty is
 * how "no ceiling" is expressed there. `focus` is absent because it is not a control
 * at all — it resolves against the selected node, which the parser cannot see.
 */
export interface RunControls {
  moves: number
  checks: number | 'every' | undefined
  panels: number
  audits: number
  /**
   * The one control that may be absent, because its absence has exactly one
   * meaning — no conjecture passes, the default until the live evaluation for
   * this arm has run (D281) — so no caller is left guessing a default for it.
   */
  conjectures?: number
  /** In dollars, as typed. Empty means no ceiling, which is the honest default. */
  ceiling: string
  ladder: readonly DeriveOp[] | undefined
  depth: number | undefined
  until: UntilCondition | undefined
  mode: RunMode | undefined
}

/**
 * The controls a draft's commands ask for, over the controls already set.
 *
 * The ONE expression of "a typed command wins over the control beneath it", and it is
 * one function because it has two readers that must never disagree: the tune panel
 * reads it to say what the next run will do, and Explore writes the result back into
 * the controls so that the answer survives the box being cleared.
 *
 * Those two used to be separate expressions of the same rule, and only the reading one
 * existed. So a chip rendered from the draft promised `/check every /spend 6`, Explore
 * replaced the draft with the clean seed, the spec fell back to controls nobody had
 * written, and the run made three checks under no ceiling at all — less grounding and
 * an unbounded spend, from a control that had said otherwise a moment earlier. Writing
 * this result back is what makes the chip a promise; keeping it one function is what
 * stops the two halves drifting again.
 *
 * Absent stays absent: a command that was not typed must not overwrite a control the
 * user set by hand. That is the same last-wins rule the parser follows inside one
 * draft, applied across the boundary between a draft and the tune panel.
 */
export function applySpec(c: RunControls, sp: CommandSpec): RunControls {
  return {
    moves: sp.moves ?? c.moves,
    checks: sp.checks ?? c.checks,
    panels: sp.panels ?? c.panels,
    audits: sp.audits ?? c.audits,
    ...((sp.conjectures ?? c.conjectures) !== undefined
      ? { conjectures: sp.conjectures ?? c.conjectures }
      : {}),
    // Not passed through the field's own input filter. That filter exists to stop a
    // half-typed `5,00` reaching the run; a number the parser has already validated
    // would only be truncated by it, and silently shrinking somebody's ceiling is the
    // one direction this app does not round.
    ceiling: sp.usdMax !== undefined ? String(sp.usdMax) : c.ceiling,
    ladder: sp.ladder ?? c.ladder,
    depth: sp.depth ?? c.depth,
    until: sp.until ?? c.until,
    mode: sp.mode ?? c.mode,
  }
}

/** One recognised command, for the chip that will represent it. */
export interface CommandChip {
  /** The command name, which is also the key the chip removes. */
  name: string
  /** What it will do, in the fewest words that stay true. */
  label: string
  /** The exact source text, so removing the chip can delete precisely it. */
  source: string
}

export interface Parsed {
  /** The prose, with every recognised command removed. Safe to store as a seed. */
  seed: string
  spec: CommandSpec
  chips: CommandChip[]
  /**
   * What could not be honoured, in words a person can act on.
   *
   * Surfaced rather than swallowed. A command with a typo that vanished silently
   * would read as a command that had been applied, and the run would then not do the
   * thing its author believed they had asked for.
   */
  problems: string[]
}

/** `pair` takes one word, and a second when the first was a number — the shape of
 *  `/until 3 refuted` beside `/until refuted`. `text` takes everything to the next
 *  command or the end of the line — the shape of a directive, whose argument is a
 *  sentence. */
type Arity = 'none' | 'one' | 'pair' | 'text'

/**
 * What `apply` came back with. The distinction exists for the chips: a REFUSED
 * command must not mint one — its label would read spec state the refusal never
 * set, and a chip that says "undefined moves" is a promise about nothing — while
 * an applied-with-a-caveat command gets both the chip and the note.
 */
type Applied = null | { refuse: string } | { note: string }

interface Def {
  /** Every spelling that means this command. The first is the canonical one. */
  names: string[]
  arity: Arity
  /** Applies the argument. Mutates the spec in place only when it applies. */
  apply: (spec: CommandSpec, arg: string) => Applied
  /** The chip label, once applied. */
  label: (spec: CommandSpec) => string
  /**
   * The identity chips deduplicate on, when it is not the canonical name.
   *
   * The three frames need it: they are three commands but ONE exclusive choice,
   * and without a shared key `/question /goal` would leave two chips standing —
   * one of them describing a frame the spec no longer holds.
   */
  chipKey?: string
  /**
   * The opposite case: this command ACCUMULATES, so last-wins must not apply.
   * Two givens are two directives and two chips — deduplicating them would
   * silently drop the first from the display while the spec still carried it.
   */
  many?: boolean
}

const int = (raw: string): number | null => {
  if (!/^\d+$/.test(raw)) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** The nearest offered move count, and whether it moved. */
function snapMoves(n: number): { moves: number; moved: boolean } {
  let best = MOVE_ROWS[0] as number
  for (const row of MOVE_ROWS) if (Math.abs(row - n) < Math.abs(best - n)) best = row
  return { moves: best, moved: best !== n }
}

/** Each steerable verb under the name the buttons give it, lowercased. `Test` and
 *  `Branch` are the words the inspector and the palette put in front of somebody
 *  before they ever type one, and taking `experiment` while refusing `test` makes
 *  the typed surface speak a language the visible one does not. Built from
 *  `STEERABLE` alone, so `/only synthesis` stays refused alongside `/only bridge`
 *  — the ids remain the canonical tokens, and the refusals keep listing them. */
const BY_LABEL = new Map<string, DeriveOp>(STEERABLE.map((v) => [OP_LABEL[v].toLowerCase(), v]))

function parseVerbs(raw: string): { verbs: DeriveOp[]; bad: string[] } {
  const verbs: DeriveOp[] = []
  const bad: string[] = []
  for (const part of raw
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean)) {
    const hit = STEERABLE.find((v) => v === part) ?? BY_LABEL.get(part)
    if (!hit) bad.push(part)
    else if (!verbs.includes(hit)) verbs.push(hit)
  }
  return { verbs, bad }
}

const DEFS: Def[] = [
  {
    names: ['moves', 'move'],
    arity: 'one',
    apply: (spec, arg) => {
      const n = int(arg)
      if (n === null || n < 1) return { refuse: `“/moves ${arg}” needs a whole number of moves.` }
      const { moves, moved } = snapMoves(n)
      spec.moves = moves
      return moved
        ? { note: `A run comes in ${MOVE_ROWS.join(', ')} moves, so ${n} was taken as ${moves}.` }
        : null
    },
    label: (spec) => `${spec.moves} moves`,
  },
  {
    names: ['spend', 'ceiling'],
    arity: 'one',
    apply: (spec, arg) => {
      const cleaned = arg.replace(/^\$/, '')
      const n = Number(cleaned)
      if (!/^\d+(\.\d+)?$/.test(cleaned) || !Number.isFinite(n) || n <= 0) {
        return {
          refuse: `“/spend ${arg}” needs an amount in dollars, like /spend 8 or /spend $8.50.`,
        }
      }
      spec.usdMax = n
      return null
    },
    label: (spec) => `stop at $${spec.usdMax}`,
  },
  {
    // The extending form: money is the budget rather than the move count. It lifts
    // the moves to the largest row and sets the ceiling, so the ceiling binds first
    // whenever it is smaller than what 48 moves could draw — and when it is not,
    // the tune panel's existing ceiling note already says which bound goes first. No
    // machinery of its own, which is the point: every refusal a ceiling has (no
    // published price, below the one-pass floor) already exists and applies.
    names: ['within'],
    arity: 'one',
    apply: (spec, arg) => {
      const cleaned = arg.replace(/^\$/, '')
      const n = Number(cleaned)
      if (!/^\d+(\.\d+)?$/.test(cleaned) || !Number.isFinite(n) || n <= 0) {
        return { refuse: `“/within ${arg}” needs an amount in dollars, like /within 12.` }
      }
      spec.usdMax = n
      spec.moves = MOVE_ROWS[MOVE_ROWS.length - 1] as number
      return null
    },
    label: (spec) => `up to $${spec.usdMax}, money as the budget`,
  },
  {
    names: ['check', 'checks'],
    arity: 'one',
    apply: (spec, arg) => {
      if (arg.toLowerCase() === 'every') {
        spec.checks = 'every'
        return null
      }
      const n = int(arg)
      if (n === null) return { refuse: `“/check ${arg}” takes a number or the word every.` }
      spec.checks = n
      return null
    },
    label: (spec) =>
      spec.checks === 'every'
        ? 'check every claim it can'
        : `${plural(spec.checks as number, 'source check')}`,
  },
  {
    names: ['panel', 'panels'],
    arity: 'one',
    apply: (spec, arg) => {
      // Bare `/panel` means one. The argument is optional for this command and for
      // `/audit`, because "do this once" is overwhelmingly what somebody typing the
      // bare word means, and refusing it would be pedantry with a cost.
      if (!arg) {
        spec.panels = 1
        return null
      }
      const n = int(arg)
      if (n === null)
        return { refuse: `“/panel ${arg}” takes a number, or nothing at all for one.` }
      spec.panels = n
      return null
    },
    label: (spec) => plural(spec.panels as number, 'panel'),
  },
  {
    names: ['audit', 'audits'],
    arity: 'one',
    apply: (spec, arg) => {
      if (!arg) {
        spec.audits = 1
        return null
      }
      const n = int(arg)
      if (n === null)
        return { refuse: `“/audit ${arg}” takes a number, or nothing at all for one.` }
      spec.audits = n
      return null
    },
    label: (spec) => plural(spec.audits as number, 'cross-examination'),
  },
  {
    // Opt-in, like `/audit`, and bare means one. Not a typed act: proposing on the
    // selected node now is the node card's press (`U`), and a name that meant both
    // would break the rule the acts below keep. "Insight" is the reader's word for
    // the pass and goes first, so completion offers it; the old words stay accepted
    // so nobody's habit breaks (D333).
    names: ['insight', 'insights', 'conjecture', 'conjectures'],
    arity: 'one',
    apply: (spec, arg) => {
      if (!arg) {
        spec.conjectures = 1
        return null
      }
      const n = int(arg)
      // `apply` cannot see which spelling was typed, so it names the canonical one.
      if (n === null)
        return { refuse: `“/insight ${arg}” takes a number, or nothing at all for one.` }
      spec.conjectures = n
      return null
    },
    label: (spec) => plural(spec.conjectures as number, 'insight pass', 'insight passes'),
  },
  {
    names: ['only'],
    arity: 'one',
    apply: (spec, arg) => {
      const { verbs, bad } = parseVerbs(arg)
      if (!verbs.length) {
        return { refuse: `“/only ${arg}” needs one of ${STEERABLE.join(', ')}.` }
      }
      // One verb is the whole point, so extras are a caveat rather than a silent
      // truncation — somebody who meant several wanted `/ladder`.
      spec.ladder = [verbs[0]!]
      return bad.length
        ? {
            note: `“${bad.join(', ')}” is not a verb a run can be steered onto, so /only took ${verbs[0]}.`,
          }
        : verbs.length > 1
          ? { note: `/only takes one verb, so it took ${verbs[0]}. Use /ladder for an order.` }
          : null
    },
    label: (spec) => `only ${spec.ladder?.[0]}`,
  },
  {
    names: ['ladder', 'order'],
    arity: 'one',
    apply: (spec, arg) => {
      const { verbs, bad } = parseVerbs(arg)
      if (!verbs.length)
        return { refuse: `“/ladder ${arg}” needs verbs from ${STEERABLE.join(', ')}.` }
      spec.ladder = verbs
      return bad.length
        ? { note: `“${bad.join(', ')}” is not a verb a run can be steered onto and was left out.` }
        : null
    },
    label: (spec) => `ladder: ${spec.ladder?.join(' → ')}`,
  },
  {
    names: ['depth'],
    arity: 'one',
    apply: (spec, arg) => {
      const n = int(arg)
      if (n === null || n < 1)
        return { refuse: `“/depth ${arg}” needs a whole number of rings, one or more.` }
      spec.depth = n
      return null
    },
    label: (spec) => `${plural(spec.depth as number, 'ring')} deep`,
  },
  {
    names: ['focus'],
    arity: 'none',
    apply: (spec) => {
      spec.focus = true
      return null
    },
    label: () => 'only the selected branch',
  },
  // The three frames. Exclusive — last-wins like every duplicated command — and
  // arity `none`, because the frame is about the prose beside it: `/question Why do
  // birds migrate?` must leave every word of the question in the seed.
  ...(['claim', 'question', 'goal'] as const).map((frame): Def => ({
    names: [frame],
    arity: 'none',
    chipKey: 'frame',
    apply: (spec) => {
      spec.frame = frame
      return null
    },
    label: () => `read as a ${frame}`,
  })),
  // The two run modes, an exclusive pair shaped like the frames: last-wins,
  // arity `none`, one shared chip. `/confirm` gates every pass of the next run
  // behind the person's go; `/auto` is the walk-away run and what an untyped
  // draft already means.
  ...(['auto', 'confirm'] as const).map((mode): Def => ({
    names: [mode],
    arity: 'none',
    chipKey: 'mode',
    apply: (spec) => {
      spec.mode = mode
      return null
    },
    label: () => (mode === 'confirm' ? 'asks before each move' : 'runs on its own'),
  })),
  // The standing brief. Text arity, accumulating: several givens are several
  // givens, so these are the one family the last-wins chip rule must not touch —
  // see `manyKey` on the chip minting.
  ...(['constraint', 'given', 'exclude', 'criterion'] as const).map((kind): Def => ({
    names: kind === 'exclude' ? ['exclude', 'avoid'] : [kind],
    arity: 'text',
    many: true,
    apply: (spec, arg) => {
      if (!arg)
        return {
          refuse: `“/${kind}” needs the words of the directive after it, like /${kind === 'given' ? 'given the catalyst is stable' : kind === 'constraint' ? 'constraint under $50k' : kind === 'exclude' ? 'exclude anything cryogenic' : 'criterion a bench result within a week'}.`,
        }
      ;(spec.directives ??= []).push({ kind, text: arg })
      return null
    },
    label: (spec) => {
      const d = spec.directives?.filter((x) => x.kind === kind).at(-1)
      const t = d?.text ?? ''
      return `${kind}: ${t.length > 34 ? `${t.slice(0, 33)}…` : t}`
    },
  })),
  {
    // A second opening of the same idea by the other house, blind to the first —
    // see `runSeed`. Distinct from /audit, which ATTACKS what one model wrote;
    // this one opens the idea independently, so the graph holds both houses'
    // first moves side by side, each node stamped with its author.
    names: ['replicate'],
    arity: 'none',
    apply: (spec) => {
      spec.replicate = true
      return null
    },
    label: () => 'opened twice, once per house',
  },
  {
    names: ['until'],
    arity: 'pair',
    apply: (spec, arg) => {
      const [a = '', b = ''] = arg.split(' ')
      const n = /^\d+$/.test(a) ? Number(a) : 1
      const word = (/^\d+$/.test(a) ? b : a).toLowerCase()
      const kind = UNTIL_WORDS[word]
      if (!kind) {
        return {
          refuse: `“/until ${arg}” needs a count and one of refuted, supported, split, objection — like /until 3 refuted.`,
        }
      }
      if (n < 1) return { refuse: `“/until ${arg}” needs a count of one or more.` }
      spec.until = { kind, n }
      return null
    },
    label: (spec) => `until ${untilLabel(spec.until as UntilCondition)}`,
  },
]

/**
 * The words `/until` accepts, mapped onto the four condition kinds.
 *
 * Only facts from the grounding record are nameable — see `UntilCondition` for
 * why. There is deliberately no word here for novelty, confidence, insight or any
 * other rating the model gives its own work: a run that stopped on those would be
 * following its own enthusiasm, at the user's expense, wearing their command as
 * authorisation. Nor for a count of insights (the reader's word for a conjecture,
 * D333): `/until 3 insights` would stop a run on how much the model proposed, so
 * it stays refused like the rest.
 */
const UNTIL_WORDS: Record<string, UntilCondition['kind'] | undefined> = {
  refuted: 'refuted',
  disputed: 'refuted',
  supported: 'supported',
  split: 'split',
  splits: 'split',
  objection: 'objection',
  objections: 'objection',
}

const BY_NAME = new Map<string, Def>()
for (const def of DEFS) for (const n of def.names) BY_NAME.set(n, def)

/** Every command name, canonical first, for a completion menu. */
export const COMMAND_NAMES: string[] = DEFS.map((d) => d.names[0]!)

/** One line per command, for the help the box offers. */
export const COMMAND_HELP: Array<{ name: string; hint: string }> = [
  { name: 'question', hint: 'Read the idea as a question — branches are competing answers' },
  { name: 'goal', hint: 'Read the idea as a goal — branches are routes and blockers' },
  { name: 'claim', hint: 'Read the idea as a claim to test — the default' },
  { name: 'moves', hint: 'How many passes — 12, 24 or 48' },
  { name: 'spend', hint: 'Stop under an estimated spend, like /spend 8' },
  { name: 'within', hint: 'Money as the budget: lifts the moves, like /within 12' },
  { name: 'check', hint: 'Source checks — a number, or every' },
  { name: 'panel', hint: 'Read claims again under three lenses' },
  { name: 'audit', hint: 'Hand a branch to a model from another company' },
  {
    name: 'insight',
    hint: 'Propose where records of what does not fit gather; each searched for prior art',
  },
  { name: 'until', hint: 'Stop the moment evidence lands, like /until 3 refuted' },
  { name: 'confirm', hint: 'The run proposes each move and waits for your go' },
  { name: 'auto', hint: 'The run takes its moves on its own — the default' },
  {
    name: 'constraint',
    hint: 'A bound you actually have — routes it kills say so, and come back if you relax it',
  },
  { name: 'given', hint: 'A premise to stipulate — reasoned inside, never certified' },
  {
    name: 'exclude',
    hint: 'Territory not worth moves. Semantic; /focus is the structural version',
  },
  { name: 'criterion', hint: 'What would settle it for you — tests aim at this bar' },
  { name: 'replicate', hint: 'The other house opens the idea too, blind to the first' },
  { name: 'only', hint: 'Spend the whole run on one verb' },
  { name: 'ladder', hint: 'The order verbs are tried, like /ladder challenge,deepen' },
  { name: 'depth', hint: 'How many rings out from the seed' },
  { name: 'focus', hint: 'Work only the selected branch' },
]

/**
 * A `/name` at a word boundary — the NAME ONLY, never its argument.
 *
 * The leading `(^|\s)` is what keeps this off `https://host/path`, `and/or` and `3/4`:
 * in all three the slash follows a non-space character.
 *
 * The argument is deliberately not part of this pattern, and that is a correction
 * rather than a preference. A group that ran to the next command consumed the
 * whitespace in front of it — which is the very character the next match's `(^|\s)`
 * needed — so in `/moves 12 /moves 48` the second command did not match at all, and a
 * run of consecutive commands applied only every other one. Silently. The argument is
 * read from the remaining text instead, by `firstWord`.
 */
const TOKEN = /(^|\s)\/([a-z]+)/gi

/**
 * The argument, and how much of the text it used up.
 *
 * One word: `/panel 2 because it matters` takes `2` and leaves the clause in the
 * prose, because a command that swallowed the rest of the sentence would delete
 * somebody's idea. `consumed` counts from the start of `raw` through the argument,
 * separators included, so the caller can advance past exactly what was taken.
 */
function firstWord(raw: string): { arg: string; consumed: number } {
  const sep = /^[\s:=]+/.exec(raw)?.[0] ?? ''
  const m = /^([^\s]+)/.exec(raw.slice(sep.length))
  if (!m) return { arg: '', consumed: 0 }
  const token = m[1]!
  // An ARGUMENT IS NEVER A COMMAND. Without this, a bare `/audit` followed by
  // `/only challenge` took `/only` as its argument — so the audit reported a bad
  // argument it had never been given, AND the `/only` was consumed and never ran.
  // Returning nothing here lets the commands that default on a missing argument do
  // so, and leaves the next match to be found where it actually is.
  if (/^\/[a-z]/i.test(token)) return { arg: '', consumed: 0 }
  const arg = token.replace(/[.,;]+$/, '')
  return { arg, consumed: sep.length + token.length }
}

/**
 * The frame a draft LOOKS like it wants, or null when it reads as a claim.
 *
 * A suggestion and never a switch. Somebody who types "Why do birds migrate?" under
 * the claim frame gets a graph whose every verb is aimed at something that cannot be
 * true or false — but they may also have meant it rhetorically, and silently
 * reframing their idea would be the tool deciding what they meant. So this returns a
 * reading; the rail offers it and the person applies it.
 *
 * Deliberately narrow. It fires on the two shapes that are almost never anything
 * else — a trailing question mark with a leading interrogative, and an explicit
 * statement of wanting — and stays quiet otherwise. A suggestion that fired on
 * ordinary claims would be trained away in a day.
 */
export function suggestFrame(seed: string): Frame | null {
  const t = seed.trim()
  if (!t) return null
  if (
    /^(why|how|what|which|when|where|who|whether|does|do|is|are|can|could|should|will)\b/i.test(
      t,
    ) &&
    t.endsWith('?')
  ) {
    return 'question'
  }
  if (/^(build|design|reach|achieve|get|make|create|develop|find a way to)\b/i.test(t))
    return 'goal'
  if (/\b(i|we)\s+(want|need)\s+to\b/i.test(t)) return 'goal'
  return null
}

/**
 * Whether a draft is an OPEN question: the one shape the seed box reads as a
 * question before Enter, rather than only suggesting it (D123).
 *
 * Narrower than `suggestFrame` on purpose. "Why did the Bronze Age collapse?"
 * seeded as a claim got a graph aimed at something that cannot be true or false;
 * seeded as a question, the same words produced the field's competing answers.
 * A yes/no question ("Is sodium-ion cheaper?") is often a claim with a question
 * mark on it, so it stays a suggestion.
 */
export function openQuestion(seed: string): boolean {
  const t = seed.trim()
  return /^(why|how|what|which|when|where|who)\b/i.test(t) && t.endsWith('?')
}

/**
 * How the seed box reads a draft's frame, and what it offers beside it.
 *
 * A typed frame command always wins. Otherwise an open question on a blank
 * canvas is pre-filled as a question (`prefilled`: the chip is showing and its ✕
 * is `declined`), and everything else keeps the exploration's frame, with
 * `suggestFrame`'s reading offered only when it differs — and never straight
 * back after somebody took the pre-filled question off.
 */
export function seedFrameReading(o: {
  seed: string
  typed?: Frame
  current?: Frame
  overGraph: boolean
  declined: boolean
}): { frame: Frame; prefilled: boolean; suggestion: Frame | null } {
  const prefilled = !o.overGraph && !o.typed && !o.declined && openQuestion(o.seed)
  const frame = o.typed ?? (prefilled ? 'question' : (o.current ?? 'claim'))
  const hinted = o.typed ? null : suggestFrame(o.seed)
  const suggestion =
    hinted && hinted !== frame && !(o.declined && hinted === 'question') ? hinted : null
  return { frame, prefilled, suggestion }
}

/**
 * Pull the commands out of what somebody typed.
 *
 * Order of precedence inside one draft is last-wins, which is what makes editing feel
 * right: typing `/moves 12` after `/moves 48` should mean twelve, not a complaint.
 */
export function parseCommands(input: string): Parsed {
  const spec: CommandSpec = {}
  const chips: CommandChip[] = []
  const problems: string[] = []
  // Rebuilt rather than spliced, because every match's offsets shift the moment one
  // is removed and an index-based edit would drift by exactly the amount it deleted.
  let seed = ''
  let cursor = 0

  // `matchAll` clones the regex but COPIES the receiver's `lastIndex` onto the clone,
  // so a truthy `hasCommands` — whose `test` parks `lastIndex` at the end of its first
  // match — would make this loop start mid-string and silently feed every earlier
  // command into the seed, and therefore into the title. Reset here rather than in
  // `hasCommands`, so no caller ordering can reintroduce it.
  TOKEN.lastIndex = 0
  for (const m of input.matchAll(TOKEN)) {
    const [whole, lead = '', rawName = ''] = m
    const at = m.index ?? 0
    // Inside a previous command's argument, so not a command in its own right.
    if (at < cursor) continue

    const def = BY_NAME.get(rawName.toLowerCase())
    if (!def) {
      // Left in the prose on purpose, and `cursor` is deliberately NOT advanced, so
      // the text flows into the seed untouched. A word this module does not know is
      // likelier to be somebody's own slash than a mistyped command, and cutting it
      // out of their sentence is the worse of the two failures. Named anyway, so a
      // genuine typo is visible rather than silently inert.
      problems.push(`“/${rawName}” is not a command, so it was left in the idea.`)
      continue
    }

    const nameEnd = at + whole.length
    let { arg, consumed } =
      def.arity === 'none' ? { arg: '', consumed: 0 } : firstWord(input.slice(nameEnd))
    // Text arity: everything to the next command, a newline, or the end of the
    // draft — whichever comes first. A directive is a sentence, and it may
    // legally contain a slash ("under $50/kg"): the boundary is the same
    // whitespace-then-/word shape every command match requires, so an inline
    // slash never ends it.
    //
    // Nobody can type or paste a newline into the composer: it is a single-line
    // `<input>`, whose value sanitisation strips CR and LF whatever writes them.
    // So for what a person types there, the next command is the ONLY boundary,
    // and prose is safe only BEFORE the first /command: words typed after a text
    // directive join it, silently and with no entry in `problems`. The newline
    // stop stays because this function is pure and its input is any string — a
    // test, a draft put in the box from elsewhere, or a multi-line surface added
    // later — and without it one directive would swallow every line below it.
    if (def.arity === 'text') {
      const next = /(^|\s)\/(?=[a-z])/gi
      next.lastIndex = nameEnd
      const nm = next.exec(input)
      const nl = input.indexOf('\n', nameEnd)
      const stop = Math.min(nm ? nm.index : input.length, nl === -1 ? input.length : nl)
      arg = input.slice(nameEnd, stop).trim().replace(/\s+/g, ' ')
      consumed = stop - nameEnd
    }
    // A pair takes a second word only when the first was a count — `/until 3
    // refuted` and `/until refuted` are both one command, and `/until 3 refuted
    // holds` must leave `holds` in the prose.
    if (def.arity === 'pair' && /^\d+$/.test(arg)) {
      const second = firstWord(input.slice(nameEnd + consumed))
      if (second.arg) {
        arg = `${arg} ${second.arg}`
        consumed += second.consumed
      }
    }
    const res = def.apply(spec, arg)

    // Everything before this command, plus the whitespace that preceded it — kept so
    // removing a mid-sentence command does not weld the words either side together.
    // A refused command is consumed too: its note says exactly what was wrong, and
    // leaving `/moves lots` in a TITLE would preserve the mistake in five places.
    seed += input.slice(cursor, at) + lead
    cursor = nameEnd + consumed

    if (res && 'refuse' in res) {
      problems.push(res.refuse)
      continue
    }
    if (res) problems.push(res.note)

    const source = `/${rawName}${arg ? ` ${arg}` : ''}`
    // Last-wins: a second `/moves` replaces the first one's chip rather than adding a
    // second that contradicts it. Keyed on `chipKey` where one exists, so the three
    // frame spellings contest ONE chip rather than accumulating — and suspended
    // entirely for `many` commands, where accumulation is the meaning.
    const key = def.many ? `${def.names[0]}#${chips.length}` : (def.chipKey ?? def.names[0]!)
    const prior = def.many ? -1 : chips.findIndex((c) => c.name === key)
    const chip: CommandChip = { name: key, label: def.label(spec), source }
    if (prior >= 0) chips[prior] = chip
    else chips.push(chip)
  }
  seed += input.slice(cursor)

  return {
    // Collapsed, because removing commands leaves runs of spaces and a doubled space
    // in a title is the sort of thing nobody notices until it is on a document.
    seed: seed
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/ +\n/g, '\n')
      .trim(),
    spec,
    chips,
    problems,
  }
}

/**
 * The draft with its directives taken out and everything else left — the box
 * after a press over an open graph has moved `/given`, `/constraint`, `/exclude`
 * and `/criterion` into the brief. A `/moves 48` beside them still steers the
 * next run, and the question beside them is still to be asked, so only the
 * directives' own source text goes, each once, by the chip that parsed it.
 */
export function withoutDirectives(input: string): string {
  let rest = input
  for (const c of parseCommands(input).chips) {
    if (!/^(given|constraint|exclude|criterion)#/.test(c.name)) continue
    const at = rest.indexOf(c.source)
    if (at >= 0) rest = `${rest.slice(0, at)}${rest.slice(at + c.source.length)}`
  }
  return rest.replace(/[ \t]{2,}/g, ' ').trim()
}

/** Whether a draft has anything for this module to do. Cheap, for a render path. */
export const hasCommands = (input: string): boolean => {
  TOKEN.lastIndex = 0
  return TOKEN.test(input)
}

/* ── Typed acts ─────────────────────────────────────────────────────────────
   The commands above STEER THE NEXT RUN; the acts below are the panel's own
   presses, typed — a verb on the selected node, now. Two vocabularies on one
   box, so the one hard rule is that no name may mean both: `/panel 2` has
   always scheduled panels for a run and a chip already promises so, which is
   why the immediate forms of the two checks take their reading-vocabulary
   names (`/lenses`, `/examine`) rather than overloading `/panel` and `/audit`
   into meaning one thing bare and another with an argument. */

/** What a typed act can be: the six moves, the three checks, and the two free
 *  forms. The names mirror the node card's tiers — the card is where each of
 *  these is already explained and priced. */
export type ActKind = RunVerb | 'verify' | 'panel' | 'audit' | 'record' | 'edit'

/** Every spelling of each act. First is canonical, and for the six moves the
 *  canonical spelling is the BUTTON'S word (`/test`, `/branch`) for `BY_LABEL`'s
 *  reason: taking `experiment` while refusing `test` makes the typed surface
 *  speak a language the visible one does not. */
const ACT_NAMES: Array<{ names: string[]; kind: ActKind }> = [
  ...LADDER.map((v) => ({
    names: [...new Set([OP_LABEL[v].toLowerCase(), v])],
    kind: v as ActKind,
  })),
  // `/check` belongs to the run steering above; the immediate form takes the
  // pass's own name.
  { names: ['verify'], kind: 'verify' },
  { names: ['lenses'], kind: 'panel' },
  { names: ['examine', 'crossexamine'], kind: 'audit' },
  { names: ['record'], kind: 'record' },
  { names: ['edit', 'rewrite'], kind: 'edit' },
]

const ACT_BY_NAME = new Map<string, ActKind>()
for (const a of ACT_NAMES) for (const n of a.names) ACT_BY_NAME.set(n, a.kind)

/**
 * The act a draft IS, or null. Deliberately strict: the whole trimmed draft
 * must be one bare `/name`, because an act takes no argument — `/deepen but
 * gently` is somebody's sentence, and the parser above already has the right
 * answer for a slash it does not own (leave it in the prose). The caller
 * resolves the act against the selection, which this module cannot see — the
 * same division `/focus` keeps.
 */
export function parseAct(input: string): { kind: ActKind } | null {
  const m = /^\/([a-z]+)$/i.exec(input.trim())
  if (!m) return null
  const kind = ACT_BY_NAME.get(m[1]!.toLowerCase())
  return kind === undefined ? null : { kind }
}

/** One line per act, for the completion menu — offered only over an open
 *  graph, where a selection exists for them to act on. The six moves quote
 *  `OP_HINT`, the card's own glosses, so the menu and the buttons cannot
 *  drift into describing the same verb two ways. */
export const ACT_HELP: Array<{ name: string; hint: string }> = [
  ...LADDER.map((v) => ({
    name: OP_LABEL[v].toLowerCase(),
    hint: `${OP_HINT[v]} — a paid pass on the selected node`,
  })),
  { name: 'verify', hint: 'Check the selected claim against sources — a paid pass, now' },
  { name: 'lenses', hint: 'Read the selected claim under three lenses — a paid pass, now' },
  { name: 'examine', hint: 'Hand the selected claim to the other provider — a paid pass, now' },
  { name: 'record', hint: 'Write down what actually happened — free, outranks the model' },
  { name: 'edit', hint: 'Rewrite the selected claim in your words — free to write' },
]
